<template>
    <!-- Add Document Button -->
    <v-row class="mt-3"
        ><v-col>
            <v-btn color="info" class="mb-4" @click="dialogAddDoc = true">
                {{ isPhotoAndAnnexMode ? "Adaugă altele" : "Adaugă Document" }}
            </v-btn>

            <!-- Dialog for Adding Multiple Document Types -->
            <v-dialog v-model="dialogAddDoc" max-width="600px">
                <v-card>
                    <v-card-title class="text-h6"
                        >Adaugă Documente</v-card-title
                    >

                    <v-card-text>
                        <!-- Multi-select Document Types -->
                        <v-select
                            v-if="!isPhotoAndAnnexMode"
                            v-model="selectedDocKeys"
                            :items="filteredDocumentOptions"
                            item-title="label"
                            item-value="key"
                            label="Tipuri Documente"
                            variant="outlined"
                            multiple
                            chips
                            closable-chips
                            @update:modelValue="onDocTypeUpdate"
                        />

                        <!-- File Inputs Per Selected Type -->
                        <div
                            v-for="key in selectedDocKeys"
                            :key="key"
                            class="mt-4"
                        >
                            <drop-zone-uploader
                                v-model="filesByType[key]"
                                :accept="key === 'offer' ? '.xls,.xlsx' : '*/*'"
                                :label="`Fișiere pentru ${getLabel(key)}`"
                            />
                        </div>
                    </v-card-text>

                    <v-card-actions>
                        <v-spacer />
                        <v-btn text color="danger" @click="dialogAddDoc = false"
                            >Anulează</v-btn
                        >
                        <v-btn
                            color="success"
                            :disabled="!hasAtLeastOneFile"
                            @click="handleAddDoc"
                            >Adaugă</v-btn
                        >
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-col></v-row
    >

    <v-row class="justify-start">
        <!-- Iterate over document options -->
        <template
            v-for="(option, index) in visibleDocumentOptions"
            :key="index"
        >
            <!-- Iterate over existing files for this document type -->
            <template
                v-for="(file, fileIndex) in getDocumentFiles(option.key)"
                :key="`${option.key}-${fileIndex}`"
            >
                <v-col cols="auto">
                    <!-- Card with file -->
                    <v-card
                        class="document-card mb-2 d-flex flex-column justify-content-evenly"
                        style="position: relative"
                        :color="getFileCardColor(file)"
                        @mouseenter="
                            setHoveredCard(`${option.key}-${fileIndex}`, true)
                        "
                        @mouseleave="
                            setHoveredCard(`${option.key}-${fileIndex}`, false)
                        "
                        @click="viewDocument(file)"
                    >
                        <v-card-title
                            class="text-pre-wrap"
                            style="font-size: 1rem; line-height: 1.25rem"
                        >
                            {{ option.label }}
                            {{
                                getDocumentFiles(option.key).length > 1
                                    ? fileIndex + 1
                                    : ""
                            }}
                        </v-card-title>
                        <v-checkbox
                            v-if="
                                option.key === 'offer' &&
                                file.status === 'pending' &&
                                this.userPermissions.greenHouseSolarManagement
                                    .canEditClient
                            "
                            class="mx-2"
                            density="compact"
                            hide-details
                            label="Aprobă"
                            @update:modelValue="() => approveOfferFile(file)"
                        />
                        <v-card-subtitle
                            v-if="
                                option.key === 'offer' &&
                                file.status === 'pending' &&
                                !this.userPermissions.greenHouseSolarManagement
                                    .canEditClient &&
                                this.userPermissions.greenHouseSolarManagement
                                    .canEditClientOffer
                            "
                        >
                            Necesită aprobare
                        </v-card-subtitle>
                        <v-card-subtitle v-if="fileNeedsSave(file)">
                            Necesită salvare
                        </v-card-subtitle>

                        <!-- Delete Button -->
                        <v-btn
                            class="ml-1"
                            style="position: absolute; top: 0; right: 0"
                            color="error"
                            size="small"
                            variant="plain"
                            icon
                            @click.stop="deleteFile(option.key, file)"
                        >
                            <v-icon>mdi-close</v-icon>
                            <v-tooltip location="top" activator="parent">
                                Ștergere
                            </v-tooltip>
                        </v-btn>

                        <!-- View Button -->
                        <v-tooltip
                            v-if="!fileNeedsSave(file)"
                            location="top"
                            v-model="hoverStates[`${option.key}-${fileIndex}`]"
                        >
                            <template v-slot:activator="{ props }">
                                <v-card-actions
                                    class="d-flex justify-content-end"
                                    style="
                                        position: absolute;
                                        bottom: 5px;
                                        right: 10px;
                                    "
                                    v-bind="props"
                                >
                                    <v-icon title="Click to view document"
                                        >mdi-eye-outline</v-icon
                                    >
                                </v-card-actions>
                            </template>
                            Vizualizare
                        </v-tooltip>
                    </v-card>
                </v-col>
            </template>

            <!-- Placeholder if no files -->
            <v-col v-if="getDocumentFiles(option.key).length === 0" cols="auto">
                <v-card
                    :disabled="isReadonly"
                    class="document-card mb-2 d-flex flex-column justify-content-evenly"
                    style="position: relative"
                    color="warning"
                    :readonly="option.isReadonly"
                    @click="
                        !option.isReadonly && handleOpenUploadDialog(option.key)
                    "
                >
                    <v-card-title
                        class="text-pre-wrap"
                        style="font-size: 1rem; line-height: 1.25rem"
                    >
                        {{ option.label }}
                    </v-card-title>
                    <v-card-actions
                        class="d-flex justify-content-end"
                        style="position: absolute; bottom: 5px; right: 10px"
                    >
                        Fără fișier
                    </v-card-actions>
                </v-card>
                <!-- Hidden File Input -->
                <v-dialog
                    v-model="dialogVisible"
                    v-if="dialogVisible && activeUploadDialog === option.key"
                    max-width="600px"
                >
                    <v-card>
                        <v-card-title class="text-h6">
                            Adaugă documente pentru {{ option.label }}
                        </v-card-title>

                        <v-card-text>
                            <template
                                v-if="
                                    isHandheld &&
                                    option.key === 'goods_delivery_annex'
                                "
                            >
                                <div
                                    class="d-flex flex-column align-items-center"
                                >
                                    <input
                                        v-if="
                                            option.key ===
                                            'goods_delivery_annex'
                                        "
                                        type="file"
                                        ref="cameraInput"
                                        accept="image/*"
                                        capture="environment"
                                        multiple
                                        style="display: none"
                                        @change="
                                            onCameraCapture($event, option.key)
                                        "
                                    />

                                    <!-- Visible button calls a method -->
                                    <v-btn
                                        color="primary"
                                        class="mb-2"
                                        @click="triggerCameraInput()"
                                    >
                                        <v-icon left>mdi-camera</v-icon>
                                        Adaugă poză
                                    </v-btn>

                                    <v-divider />
                                    <strong>SAU</strong>
                                    <v-divider />
                                </div>
                            </template>
                            <drop-zone-uploader
                                v-model="filesByType[option.key]"
                                :accept="
                                    option.key === 'offer'
                                        ? '.xls,.xlsx'
                                        : '*/*'
                                "
                                :label="` Trage fișiere aici `"
                                :multiple="true"
                            />
                        </v-card-text>

                        <v-card-actions>
                            <v-spacer />
                            <v-btn
                                text
                                color="danger"
                                @click="cancelDialogUpload(option.key)"
                                >Anulează</v-btn
                            >
                            <v-btn
                                color="success"
                                :disabled="!filesByType[option.key]?.length"
                                @click="confirmDialogUpload(option.key)"
                            >
                                Adaugă
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-col>
        </template>
    </v-row>
</template>

<script>
import { showAlertModal } from "@/utils/utils";
import { mapGetters } from "vuex";
import DropZoneUploader from "@/components/common/DropZoneUploader.vue";
import apiClient from "@/utils/apiClient";

export default {
    name: "DocumentManager",
    components: { DropZoneUploader },
    props: {
        documentOptions: {
            type: Array,
            required: true,
        },
        documents: {
            type: Object,
            required: true,
        },
        isReadonly: {
            type: Boolean,
            default: false,
        },
        isPhotoAndAnnexMode: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        "add-file",
        "delete-file",
        "view-document",
        "update-contract-status",
    ],

    data() {
        return {
            dialogVisible: false,

            activeUploadDialog: null,
            hoverStates: {},
            dialogAddDoc: false,
            selectedDocKeys: [],
            filesByType: {},
            statusMapping: {
                advance_invoice: "Factură avans achitată",
                connection_certificate: "Certificat de racordare",
                final_invoice: "Factură finală",
            },
        };
    },
    computed: {
        ...mapGetters(["userPermissions", "userRole"]),
        isHandheld() {
            console.log(window.matchMedia("(pointer: coarse)").matches)
            return (
                this.$vuetify.display.mdAndDown || // narrow viewports
                window.matchMedia("(pointer: coarse)").matches
            ); // any touchscreen
        },
        /**
         * Document cards that should actually render.
         * If we’re in normal mode **and** the current user has the
         * “acquisitions-only” role, show **only** “Anexa marfă trimisă”.
         */
        visibleDocumentOptions() {
            const onlyAnnex =
                !this.isPhotoAndAnnexMode &&
                (this.userRole || []).includes("acquisitionsOnly");

            return onlyAnnex
                ? this.documentOptions.filter(
                      (opt) => opt.key === "goods_delivery_annex"
                  )
                : this.documentOptions;
        },
        /**
         * Filters document options based on user permissions.
         * If the user has only offer editing rights, limit to just the offer.
         * @returns {Array<Object>}
         */
        filteredDocumentOptions() {
            const { canEditClient, canEditClientOffer } =
                this.userPermissions.greenHouseSolarManagement;

            let opts = this.documentOptions;

            if (!canEditClient && canEditClientOffer) {
                opts = opts.filter((opt) => opt.key === "offer");
            }

            // acquisitions-only rule
            if (
                !this.isPhotoAndAnnexMode &&
                (this.userRole || []).includes("acquisitionsOnly")
            ) {
                opts = opts.filter((opt) => opt.key === "goods_delivery_annex");
            }

            return opts;
        },
        /**
         * Checks if at least one file is selected across all chosen document types.
         * @returns {boolean}
         */
        hasAtLeastOneFile() {
            return this.selectedDocKeys.some(
                (key) =>
                    Array.isArray(this.filesByType[key]) &&
                    this.filesByType[key].length > 0
            );
        },
    },
    methods: {
        /**
         * Programmatically click the hidden camera input.
         */
        triggerCameraInput() {
            const inp = this.$refs.cameraInput;
            // if it’s a Vue array of refs, take first:
            const el = Array.isArray(inp) ? inp[0] : inp;
            if (el && typeof el.click === "function") {
                el.click();
            }
        },
        onCameraCapture(evt, key) {
            // gather the new files and append to anything already chosen
            const newFiles = Array.from(evt.target.files || []);
            this.filesByType[key] = [
                ...(this.filesByType[key] || []),
                ...newFiles,
            ];

            evt.target.value = "";
        },
        /**
         * Approves an offer file by setting its status to "approved".
         *
         * Sends a multipart/form-data PATCH request to the backend.
         *
         * @param {Object} file - The file object containing at least an `id` field.
         */
        async approveOfferFile(file) {
            try {
                const formData = new FormData();
                formData.append("id", file.id);
                formData.append("status", "approved");

                const response = await apiClient.patch(
                    "/api/attachments/upload/",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    }
                );

                file.status = response.data.status;
                showAlertModal(
                    this.$store,
                    "Document approved successfully.",
                    "success",
                    3000
                );
            } catch (error) {
                console.error("Approval failed:", error);
                showAlertModal(
                    this.$store,
                    "Failed to approve document.",
                    "danger",
                    5000
                );
            }
        },
        /**
         * Determines the card color based on file status.
         *
         * @param {Object} file
         * @returns {string} Vuetify color class
         */
        getFileCardColor(file) {
            if (this.fileNeedsSave(file)) return "grey lighten-1";
            if (file.status === "pending") return "info";
            return "success";
        },
        cancelDialogUpload(key) {
            if (this.filesByType[key]) {
                this.filesByType[key] = []; // ✅ Resetează fișierele pentru acel key
            }
            this.dialogVisible = false; // ✅ Închide dialogul
            this.activeUploadDialog = null;
        },
        handleOpenUploadDialog(key) {
            this.activeUploadDialog = key;
            this.dialogVisible = true;
        },
        confirmDialogUpload(key) {
            const files = this.filesByType[key] || [];

            if (!files.length) return;

            if (key === "offer") {
                const invalidFiles = files.some(
                    (f) => !/\.(xls|xlsx)$/i.test(f.name)
                );
                if (invalidFiles) {
                    showAlertModal(
                        this.$store,
                        "Doar fișiere Excel (.xls, .xlsx) sunt permise pentru 'Ofertă'.",
                        "warning",
                        4000
                    );
                    return;
                }
            }

            this.dialogVisible = false;
            this.activeUploadDialog = null;

            this.$emit("add-file", { key, files });

            if (this.statusMapping[key] && key !== "offer") {
                this.$emit("update-contract-status", this.statusMapping[key]);
            }
        },
        /**
         * Called when selectedDocKeys is updated.
         * Cleans up filesByType for document types no longer selected.
         * @param {string[]} updatedKeys
         */
        onDocTypeUpdate(updatedKeys) {
            const removedKeys = Object.keys(this.filesByType).filter(
                (key) => !updatedKeys.includes(key)
            );

            removedKeys.forEach((key) => {
                delete this.filesByType[key]; // Use native delete in Vue 3
            });

            this.selectedDocKeys = updatedKeys;
        },
        /**
         * Resets dialog state on cancel.
         */
        resetDialog() {
            this.selectedDocKeys = [];
            this.filesByType = {};
        },
        /**
         * Removes a document type from selection and clears its files.
         * @param {string} key
         */
        removeDocumentType(key) {
            this.selectedDocKeys = this.selectedDocKeys.filter(
                (k) => k !== key
            );
            this.$delete(this.filesByType, key);
        },
        /**
         * Returns the label for a given document type key.
         * @param {string} key
         * @returns {string}
         */
        getLabel(key) {
            const doc = this.documentOptions.find((d) => d.key === key);
            return doc ? doc.label : key;
        },

        /**
         * Handles adding multiple document types and their files.
         * Emits each valid one to parent.
         */
        handleAddDoc() {
            if (this.isPhotoAndAnnexMode) {
                const files = this.filesByType["other"] || [];

                if (!files.length) {
                    showAlertModal(
                        this.$store,
                        "Selectați cel puțin un fișier.",
                        "warning",
                        4000
                    );
                    return;
                }

                this.$emit("add-file", { key: "other", files });
                this.dialogAddDoc = false;
                this.selectedDocKeys = [];
                this.filesByType = {};
                return;
            }

            // Regular mode
            if (!this.selectedDocKeys.length) {
                showAlertModal(
                    this.$store,
                    "Selectați cel puțin un tip de document.",
                    "warning",
                    4000
                );
                return;
            }

            for (const key of this.selectedDocKeys) {
                const files = this.filesByType[key] || [];

                if (!files.length) continue;

                if (key === "offer") {
                    const allowedExtensions = ["xls", "xlsx"];
                    const invalidFiles = files.some(
                        (file) =>
                            !allowedExtensions.includes(
                                file.name.split(".").pop().toLowerCase()
                            )
                    );
                    if (invalidFiles) {
                        showAlertModal(
                            this.$store,
                            "Doar fișiere Excel (.xls, .xlsx) sunt permise pentru 'Ofertă'.",
                            "warning",
                            5000
                        );
                        continue;
                    }
                }

                this.$emit("add-file", { key, files });

                if (this.statusMapping[key] && key !== "offer") {
                    this.$emit(
                        "update-contract-status",
                        this.statusMapping[key]
                    );
                }
            }

            // Reset state after emit
            this.dialogAddDoc = false;
            this.selectedDocKeys = [];
            this.filesByType = {};
        },
        getDocumentFiles(documentType) {
            return this.documents[documentType] || [];
        },
        fileNeedsSave(file) {
            return file.isFileField || !file.id;
        },
        triggerFileInput(key) {
            this.$refs["fileInputs" + key][0].click();
        },
        addFiles(event, key) {
            const files = Array.from(event.target.files);

            // Restrict file type for "offer" to only Excel files
            if (key === "offer") {
                const allowedExtensions = ["xls", "xlsx"];
                const invalidFiles = files.some(
                    (file) =>
                        !allowedExtensions.includes(
                            file.name.split(".").pop().toLowerCase()
                        )
                );

                if (invalidFiles) {
                    showAlertModal(
                        this.$store,
                        "Doar fișiere Excel (.xls, .xlsx) sunt permise pentru 'Ofertă'.",
                        "warning",
                        5000
                    );
                    event.target.value = ""; // Reset input
                    return;
                }
            }

            this.$emit("add-file", { key, files });

            //  Emit event only if the file belongs to one of the tracked categories
            if (this.statusMapping[key] && key !== "offer") {
                this.$emit("update-contract-status", this.statusMapping[key]);
            }

            event.target.value = ""; // Reset input
        },
        deleteFile(documentType, file) {
            this.$emit("delete-file", { documentType, file });
        },
        viewDocument(document) {
            this.$emit("view-document", document);
        },
        setHoveredCard(key, state) {
            this.hoverStates[key] = state;
        },
    },
    watch: {
        /**
         * Watches the dialog visibility and resets form if closed.
         * This ensures cleanup even if closed by clicking outside.
         * @param {boolean} newVal
         * @param {boolean} oldVal
         */
        dialogAddDoc(newVal, oldVal) {
            if (newVal && this.isPhotoAndAnnexMode) {
                this.selectedDocKeys = ["other"];
            }
            if (!newVal && oldVal) {
                this.resetDialog();
            }
        },
        dialogVisible(newVal, oldVal) {
            if (!newVal && oldVal && this.activeUploadDialog) {
                this.cancelDialogUpload(this.activeUploadDialog);
            }
        },
    },
};
</script>

<style scoped>
.gray-background:hover {
    background: rgba(30, 30, 30, 0.1);
}

.gray-background {
    background: rgba(30, 30, 30, 0.1);
}

.document-card {
    cursor: pointer;
    transition: background-color 0.2s ease-in-out;
    width: 140px; /* Fixed width for all cards */
    height: 199px; /* Fixed height for all cards */
}
</style>
